import React, { useState } from "react";
import { LockKeyhole, AlertCircle, Mail, KeyRound } from "lucide-react";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { useAgencyNavigate } from "../lib/agency";

export default function Login() {
  const [bookingId, setBookingId] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // "form" | "email" | "forgot-request" | "forgot-verify"
  const [email, setEmail] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const navigate = useAgencyNavigate();

  const requestCode = async (e) => {
    e.preventDefault();
    setErr("");
    setForgotMsg("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/email-code/request", { email: forgotEmail });
      setForgotMsg(data.detail);
      setStep("forgot-verify");
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo enviar el código");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/email-code/verify", { email: forgotEmail, code: forgotCode });
      auth.save(data);
      navigate("/dashboard");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Código incorrecto o vencido");
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await auth.login(bookingId, lastName, password);
      auth.save(data);
      if (data.needs_email) {
        setStep("email");
      } else {
        navigate("/dashboard");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/me/email", { email });
      navigate("/dashboard");
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo guardar tu correo");
    } finally {
      setLoading(false);
    }
  };

  if (step === "forgot-request") {
    return (
      <Layout>
        <section className="min-h-[calc(100dvh-4rem)] flex items-center justify-center px-6 py-16">
          <div className="w-full max-w-md fade-up">
            <div className="text-center mb-10">
              <div className="inline-flex w-14 h-14 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/30 items-center justify-center mb-6">
                <KeyRound className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
              </div>
              <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">¿Olvidaste tu acceso?</p>
              <h1 className="font-display text-3xl font-light tracking-tight">Entra con tu correo</h1>
              <p className="text-sm text-neutral-400 mt-3">
                Solo funciona si ya dejaste tu correo antes. Te mandamos un código de 6 dígitos.
              </p>
            </div>
            <form onSubmit={requestCode} className="space-y-7 glass rounded-3xl p-8 border border-white/10">
              <input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="tu@correo.com"
                data-testid="forgot-email-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-3 text-white placeholder:text-neutral-700 transition-colors"
                required
              />
              {err && (
                <div className="flex items-start gap-2 text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-xl p-3" data-testid="forgot-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="request-code-btn"
                className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-3.5 rounded-full font-semibold tracking-wide hover:bg-[#FF9DC0] cyan-glow transition-all disabled:opacity-50"
              >
                {loading ? "Enviando…" : "Mandar código"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("form"); setErr(""); }}
                data-testid="forgot-back-btn"
                className="block w-full text-center text-xs uppercase tracking-[0.3em] py-2 text-neutral-500 hover:text-neutral-300"
              >
                ← Volver
              </button>
            </form>
          </div>
        </section>
      </Layout>
    );
  }

  if (step === "forgot-verify") {
    return (
      <Layout>
        <section className="min-h-[calc(100dvh-4rem)] flex items-center justify-center px-6 py-16">
          <div className="w-full max-w-md fade-up">
            <div className="text-center mb-10">
              <div className="inline-flex w-14 h-14 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/30 items-center justify-center mb-6">
                <KeyRound className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
              </div>
              <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Revisa tu correo</p>
              <h1 className="font-display text-3xl font-light tracking-tight">Escribe el código</h1>
              {forgotMsg && <p className="text-sm text-neutral-400 mt-3">{forgotMsg}</p>}
            </div>
            <form onSubmit={verifyCode} className="space-y-7 glass rounded-3xl p-8 border border-white/10">
              <input
                value={forgotCode}
                onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                data-testid="forgot-code-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-3 text-white text-center text-2xl tracking-[0.5em] placeholder:text-neutral-700 transition-colors"
                required
              />
              {err && (
                <div className="flex items-start gap-2 text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-xl p-3" data-testid="verify-code-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={loading || forgotCode.length !== 6}
                data-testid="verify-code-btn"
                className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-3.5 rounded-full font-semibold tracking-wide hover:bg-[#FF9DC0] cyan-glow transition-all disabled:opacity-50"
              >
                {loading ? "Verificando…" : "Entrar"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("forgot-request"); setErr(""); setForgotCode(""); }}
                data-testid="verify-back-btn"
                className="block w-full text-center text-xs uppercase tracking-[0.3em] py-2 text-neutral-500 hover:text-neutral-300"
              >
                ← Pedir otro código
              </button>
            </form>
          </div>
        </section>
      </Layout>
    );
  }

  if (step === "email") {
    return (
      <Layout>
        <section className="min-h-[calc(100dvh-4rem)] flex items-center justify-center px-6 py-16">
          <div className="w-full max-w-md fade-up">
            <div className="text-center mb-10">
              <div className="inline-flex w-14 h-14 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/30 items-center justify-center mb-6">
                <Mail className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
              </div>
              <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Un último paso</p>
              <h1 className="font-display text-3xl font-light tracking-tight">¿Cuál es tu correo?</h1>
              <p className="text-sm text-neutral-400 mt-3">
                Te mandamos ahí el aviso legal (alcance del SOS, uso de datos) y, si quieres, tus promociones.
              </p>
            </div>

            <form onSubmit={submitEmail} className="space-y-7 glass rounded-3xl p-8 border border-white/10">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                data-testid="login-email-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-3 text-white placeholder:text-neutral-700 transition-colors"
                required
              />
              {err && (
                <div className="flex items-start gap-2 text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-xl p-3" data-testid="email-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="save-email-btn"
                className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-3.5 rounded-full font-semibold tracking-wide hover:bg-[#FF9DC0] cyan-glow transition-all disabled:opacity-50"
              >
                {loading ? "Guardando…" : "Continuar"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                data-testid="skip-email-btn"
                className="block w-full text-center text-xs uppercase tracking-[0.3em] py-2 text-neutral-500 hover:text-neutral-300"
              >
                Ahora no
              </button>
            </form>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="min-h-[calc(100dvh-4rem)] flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md fade-up">
          <div className="text-center mb-10">
            <div className="inline-flex w-14 h-14 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/30 items-center justify-center mb-6">
              <LockKeyhole className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            </div>
            <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Acceso Seguro</p>
            <h1 className="font-display text-4xl font-light tracking-tight">Bienvenido, viajero</h1>
            <p className="text-sm text-neutral-400 mt-3">Ingresa con tu Booking ID, apellido y contraseña.</p>
          </div>

          <form onSubmit={submit} className="space-y-7 glass rounded-3xl p-8 border border-white/10">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">
                ID de Viaje · Booking o Expediente
              </label>
              <input
                value={bookingId}
                onChange={(e) => setBookingId(e.target.value)}
                placeholder="REG-2026-001  o  REG-51055"
                data-testid="booking-id-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-3 text-white placeholder:text-neutral-700 transition-colors"
                required
              />
              <p className="text-[10px] text-neutral-600 mt-1.5">
                Puedes usar tu Booking ID o el ID de Expediente Maestro
              </p>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">
                Apellido
              </label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="García"
                data-testid="last-name-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-3 text-white placeholder:text-neutral-700 transition-colors"
                required
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">
                Contraseña
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="password-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-3 text-white placeholder:text-neutral-700 transition-colors"
              />
              <p className="text-[10px] text-neutral-600 mt-1.5">
                Te la mandamos por correo o WhatsApp junto con tu Booking ID. Si aún no la tienes, déjala en blanco.
              </p>
            </div>

            {err && (
              <div className="flex items-start gap-2 text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-xl p-3" data-testid="login-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-3.5 rounded-full font-semibold tracking-wide hover:bg-[#FF9DC0] cyan-glow transition-all disabled:opacity-50"
            >
              {loading ? "Verificando…" : "Iniciar Sesión"}
            </button>

            <button
              type="button"
              onClick={() => { setStep("forgot-request"); setErr(""); }}
              data-testid="forgot-access-link"
              className="block w-full text-center text-xs uppercase tracking-[0.3em] py-1 text-neutral-500 hover:text-[var(--brand-cyan,#FF85AC)]"
            >
              ¿Olvidaste tu acceso? Entra con tu correo
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
